import React, { useState, useEffect, useCallback } from 'react';
import { approvals, products as productsApi } from '../services/api.js';
import { getRiskTier } from '../utils/riskUtils';
import './ApprovalStub.css';

const AUTHORITY_LEVELS = ['junior', 'senior', 'chief'];

const LEVEL_LABELS = {
  junior: 'Junior UW',
  senior: 'Senior UW',
  chief: 'Chief UW',
};

function formatCurrency(val) {
  if (val == null || val === 0) return '—';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(val % 1_000_000 === 0 ? 0 : 1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toLocaleString()}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function riskTierClass(score) {
  return `as__tier--${getRiskTier(score)}`;
}

// ── Threshold Form ──────────────────────────────────────────────────────

function ThresholdForm({ products, onSave, onCancel, initial }) {
  const [form, setForm] = useState({
    productId: initial?.productId || '',
    authorityLevel: initial?.authorityLevel || 'junior',
    maxBindLimit: initial?.maxBindLimit || '',
    autoDeclineBelow: initial?.autoDeclineBelow ?? 14,
    autoBindAbove: initial?.autoBindAbove ?? 6,
    requiresApproval: initial?.requiresApproval ?? true,
    escalationTarget: initial?.escalationRules?.target || '',
    escalationSlaHours: initial?.escalationRules?.slaHours || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        productId: form.productId,
        authorityLevel: form.authorityLevel,
        maxBindLimit: parseFloat(form.maxBindLimit) || 0,
        autoDeclineBelow: parseInt(form.autoDeclineBelow, 10) || 0,
        autoBindAbove: parseInt(form.autoBindAbove, 10) || 0,
        requiresApproval: form.requiresApproval,
      };
      if (form.escalationTarget || form.escalationSlaHours) {
        payload.escalationRules = {
          target: form.escalationTarget,
          slaHours: parseInt(form.escalationSlaHours, 10) || 0,
        };
      }
      await onSave(payload);
    } catch (err) {
      setError(err.message || 'Failed to save threshold');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="as__form card" onSubmit={handleSubmit}>
      <h3 className="as__form-title">{initial ? 'Edit Threshold' : 'New Threshold'}</h3>

      {error && <div className="as__form-error">{error}</div>}

      <div className="as__form-grid">
        <label className="as__form-field">
          <span className="as__form-label">Product</span>
          <select
            value={form.productId}
            onChange={(e) => handleChange('productId', e.target.value)}
            required
            disabled={!!initial}
          >
            <option value="">Select product...</option>
            {products.map((p) => (
              <option key={p.ProductID || p.productId} value={p.ProductID || p.productId}>
                {p.Name || p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="as__form-field">
          <span className="as__form-label">Authority Level</span>
          <select
            value={form.authorityLevel}
            onChange={(e) => handleChange('authorityLevel', e.target.value)}
            required
            disabled={!!initial}
          >
            {AUTHORITY_LEVELS.map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABELS[l]}
              </option>
            ))}
          </select>
        </label>

        <label className="as__form-field">
          <span className="as__form-label">Max Bind Limit ($)</span>
          <input
            type="number"
            value={form.maxBindLimit}
            onChange={(e) => handleChange('maxBindLimit', e.target.value)}
            placeholder="e.g. 250000"
            min="0"
            step="1000"
          />
        </label>

        <label className="as__form-field">
          <span className="as__form-label">Auto-Decline Below (score)</span>
          <input
            type="number"
            value={form.autoDeclineBelow}
            onChange={(e) => handleChange('autoDeclineBelow', e.target.value)}
            min="0"
            max="20"
          />
        </label>

        <label className="as__form-field">
          <span className="as__form-label">Auto-Bind Above (score)</span>
          <input
            type="number"
            value={form.autoBindAbove}
            onChange={(e) => handleChange('autoBindAbove', e.target.value)}
            min="0"
            max="20"
          />
        </label>

        <label className="as__form-field as__form-field--checkbox">
          <input
            type="checkbox"
            checked={form.requiresApproval}
            onChange={(e) => handleChange('requiresApproval', e.target.checked)}
          />
          <span className="as__form-label">Requires Approval</span>
        </label>
      </div>

      <details className="as__form-escalation">
        <summary>Escalation Rules</summary>
        <div className="as__form-grid">
          <label className="as__form-field">
            <span className="as__form-label">Escalation Target Role</span>
            <input
              type="text"
              value={form.escalationTarget}
              onChange={(e) => handleChange('escalationTarget', e.target.value)}
              placeholder="e.g. chief_underwriter"
            />
          </label>
          <label className="as__form-field">
            <span className="as__form-label">SLA (hours)</span>
            <input
              type="number"
              value={form.escalationSlaHours}
              onChange={(e) => handleChange('escalationSlaHours', e.target.value)}
              placeholder="e.g. 24"
              min="0"
            />
          </label>
        </div>
      </details>

      <div className="as__form-actions">
        <button type="button" className="as__btn as__btn--secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="as__btn as__btn--primary" disabled={saving}>
          {saving ? 'Saving...' : initial ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}

// ── Main Component ──────────────────────────────────────────────────────

export function ApprovalStub() {
  const [thresholds, setThresholds] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('thresholds');
  const [showForm, setShowForm] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [thresholdRes, overrideRes, productRes] = await Promise.all([
        approvals.listThresholds(),
        approvals.listOverrides().catch(() => ({ data: [] })),
        productsApi.list(),
      ]);

      const tData = thresholdRes?.data || thresholdRes || [];
      const oData = overrideRes?.data || overrideRes || [];
      const pData = productRes?.data || productRes || [];

      setThresholds(Array.isArray(tData) ? tData : []);
      setOverrides(Array.isArray(oData) ? oData : []);
      setProductList(Array.isArray(pData) ? pData : []);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveThreshold = async (payload) => {
    await approvals.createThreshold(payload);
    setShowForm(false);
    setEditingThreshold(null);
    loadData();
  };

  const handleDeleteThreshold = async (productId, authorityLevel) => {
    if (!window.confirm(`Delete ${authorityLevel} threshold for this product?`)) return;
    try {
      await approvals.deleteThreshold(productId, authorityLevel);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete threshold');
    }
  };

  // Group thresholds by product - handle PascalCase API fields
  const thresholdsByProduct = {};
  thresholds.forEach((t) => {
    const key = t.ProductID || t.productId;
    if (!thresholdsByProduct[key]) {
      thresholdsByProduct[key] = {
        productId: key,
        productName: t.ProductName || t.productName || key,
        items: [],
        escalationRules: null,
      };
    }
    // Normalize to consistent field names for the UI
    const normalized = {
      ...t,
      productId: key,
      productName: t.ProductName || t.productName || key,
      authorityLevel: t.AuthorityLevel || t.authorityLevel,
      maxBindLimit: t.MaxBindLimit ?? t.maxBindLimit ?? 0,
      autoBindAbove: t.AutoBindAbove ?? t.autoBindAbove ?? 6,
      autoDeclineBelow: t.AutoDeclineBelow ?? t.autoDeclineBelow ?? 14,
      requiresApproval: t.RequiresApproval ?? t.requiresApproval ?? false,
      escalationRules: t.EscalationRules || t.escalationRules || {},
    };
    thresholdsByProduct[key].items.push(normalized);
    if (normalized.escalationRules && Object.keys(normalized.escalationRules).length > 0) {
      thresholdsByProduct[key].escalationRules = normalized.escalationRules;
    }
  });

  if (loading) {
    return (
      <div className="as">
        <div className="as__loading card">
          <div className="as__spinner" />
          <span>Loading approval boundaries...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="as">
      {/* Header */}
      <div className="as__header">
        <div>
          <h1 className="as__title">Approval Boundaries</h1>
          <p className="as__subtitle">
            Manage authority levels, bind limits, and automation thresholds
          </p>
        </div>
        {activeTab === 'thresholds' && (
          <button
            className="as__btn as__btn--primary"
            onClick={() => {
              setShowForm(true);
              setEditingThreshold(null);
            }}
          >
            + New Threshold
          </button>
        )}
      </div>

      {error && <div className="as__error card">{error}</div>}

      {/* Tabs */}
      <div className="as__tabs">
        <button
          className={`as__tab ${activeTab === 'thresholds' ? 'as__tab--active' : ''}`}
          onClick={() => setActiveTab('thresholds')}
        >
          Thresholds
          <span className="as__tab-count">{thresholds.length}</span>
        </button>
        <button
          className={`as__tab ${activeTab === 'overrides' ? 'as__tab--active' : ''}`}
          onClick={() => setActiveTab('overrides')}
        >
          Override History
          <span className="as__tab-count">{overrides.length}</span>
        </button>
      </div>

      {/* Form */}
      {showForm && activeTab === 'thresholds' && (
        <ThresholdForm
          products={productList}
          onSave={handleSaveThreshold}
          onCancel={() => {
            setShowForm(false);
            setEditingThreshold(null);
          }}
          initial={editingThreshold}
        />
      )}

      {/* Thresholds Tab */}
      {activeTab === 'thresholds' && (
        <div className="as__section">
          {Object.keys(thresholdsByProduct).length === 0 ? (
            <div className="as__empty card">
              <p>No approval thresholds configured yet.</p>
              <p className="as__empty-hint">
                Create thresholds to define authority levels and automation rules per product.
              </p>
            </div>
          ) : (
            Object.values(thresholdsByProduct).map((group) => (
              <div key={group.productId} className="as__product-group card">
                <div className="as__product-header">
                  <h3 className="as__product-name">{group.productName}</h3>
                  <span className="as__product-badge">
                    {group.items.length} level{group.items.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="as__table-wrap">
                  <table className="as__table">
                    <thead>
                      <tr>
                        <th>Authority Level</th>
                        <th>Max Bind Limit</th>
                        <th>Auto-Bind Above</th>
                        <th>Auto-Decline Below</th>
                        <th>Requires Approval</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items
                        .sort((a, b) => AUTHORITY_LEVELS.indexOf(a.authorityLevel) - AUTHORITY_LEVELS.indexOf(b.authorityLevel))
                        .map((t) => (
                          <tr key={t.authorityLevel}>
                            <td>
                              <span className={`as__level-badge as__level-badge--${t.authorityLevel}`}>
                                {LEVEL_LABELS[t.authorityLevel] || t.authorityLevel}
                              </span>
                            </td>
                            <td className="as__cell-currency">{formatCurrency(t.maxBindLimit)}</td>
                            <td>
                              <span className="as__score-badge as__score-badge--green">
                                &ge; {t.autoBindAbove}
                              </span>
                            </td>
                            <td>
                              <span className="as__score-badge as__score-badge--red">
                                &le; {t.autoDeclineBelow}
                              </span>
                            </td>
                            <td>
                              <span className={`as__approval-badge ${t.requiresApproval ? 'as__approval-badge--yes' : 'as__approval-badge--no'}`}>
                                {t.requiresApproval ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>
                              <div className="as__actions">
                                <button
                                  className="as__btn as__btn--ghost"
                                  onClick={() => {
                                    setEditingThreshold(t);
                                    setShowForm(true);
                                  }}
                                  title="Edit"
                                >
                                  Edit
                                </button>
                                <button
                                  className="as__btn as__btn--ghost as__btn--danger"
                                  onClick={() => handleDeleteThreshold(t.productId, t.authorityLevel)}
                                  title="Delete"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Escalation Rules */}
                {group.escalationRules && Object.keys(group.escalationRules).length > 0 && (
                  <div className="as__escalation">
                    <h4 className="as__escalation-title">Escalation Rules</h4>
                    <div className="as__escalation-rules">
                      {group.escalationRules.target && (
                        <div className="as__escalation-rule">
                          <span className="as__escalation-label">Target:</span>
                          <span>{group.escalationRules.target}</span>
                        </div>
                      )}
                      {group.escalationRules.slaHours && (
                        <div className="as__escalation-rule">
                          <span className="as__escalation-label">SLA:</span>
                          <span>{group.escalationRules.slaHours} hours</span>
                        </div>
                      )}
                      {group.escalationRules.condition && (
                        <div className="as__escalation-rule">
                          <span className="as__escalation-label">Condition:</span>
                          <span>{group.escalationRules.condition}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Overrides Tab */}
      {activeTab === 'overrides' && (
        <div className="as__section">
          {overrides.length === 0 ? (
            <div className="as__empty card">
              <p>No override records yet.</p>
              <p className="as__empty-hint">
                Overrides are recorded when an underwriter deviates from the system recommendation.
              </p>
            </div>
          ) : (
            <div className="as__table-wrap card">
              <table className="as__table as__table--overrides">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Submission / Insured</th>
                    <th>Product</th>
                    <th>Original</th>
                    <th>New Decision</th>
                    <th>Overridden By</th>
                    <th>Reason</th>
                    <th>Risk Score</th>
                  </tr>
                </thead>
                <tbody>
                  {overrides
                    .sort((a, b) => (b.CreatedAt || b.createdAt || '').localeCompare(a.CreatedAt || a.createdAt || ''))
                    .map((o) => {
                      const oid = o.OverrideID || o.overrideId || o.SK;
                      const created = o.CreatedAt || o.createdAt;
                      const insuredName = o.InsuredName || o.insuredName || '—';
                      const subId = o.SubmissionID || o.submissionId || '—';
                      const prodName = o.ProductName || o.productName || o.ProductID || o.productId || '—';
                      const origDec = o.OriginalDecision || o.originalDecision || '—';
                      const newDec = o.NewDecision || o.newDecision || '—';
                      const byName = o.OverriddenByName || o.overriddenByName || o.OverriddenBy || o.overriddenBy || '—';
                      const reason = o.Reason || o.reason || '—';
                      const score = o.RiskScore ?? o.riskScore;
                      return (
                        <tr key={oid}>
                          <td className="as__cell-date">{formatDate(created)}</td>
                          <td>
                            <div className="as__cell-submission">
                              <span className="as__insured-name">{insuredName}</span>
                              <span className="as__submission-id">{subId}</span>
                            </div>
                          </td>
                          <td>{prodName}</td>
                          <td>
                            <span className={`as__decision-badge as__decision-badge--${origDec}`}>
                              {origDec}
                            </span>
                          </td>
                          <td>
                            <span className={`as__decision-badge as__decision-badge--${newDec}`}>
                              {newDec}
                            </span>
                          </td>
                          <td>{byName}</td>
                          <td className="as__cell-reason">{reason}</td>
                          <td>
                            {score != null && score >= 0 ? (
                              <span className={`as__risk-score ${riskTierClass(score)}`}>
                                {score}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
